import { useState, useEffect } from "react";
import {
  MotifButton,
  MotifIcon,
  MotifDatePicker,
  MotifErrorMessage,
  MotifChip,
  MotifFormField,
  MotifLabel,
} from "@ey-xd/motif-react";
import { IconoirCheck, MotifNavigationIcClose24px } from "@ey-xd/motif-icon";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import InputBox from "../../../../components/Form/InputBox/InputBox";
import {
  formatUSDWithCommas,
  formatValueInMoneyFormat,
  isDatePassed,
} from "../../../../utils/common";
import {
  convertDateStringtoDate,
  formatToLocalYYYYMMDD,
  getLocalDateStringYYYYFormat,
} from "../../../../utils/dateFunction";
import "./CostUpdateRequest.scss";
import {
  INDEXATION_PLACEHOLDER,
  OLD_COST_BASIS,
  OLD_INDEXATION,
  OLD_NSR,
  OLD_COST_PUBLISH_DATE,
  NEW_COST_BASIS,
  NEW_INDEXATION,
  NEW_NSR,
  NEW_COST_PUBLISH_DATE,
} from "../../../CreateSku/Constants";
import { VALIDATION_ERROR_MESSAGES } from "../../../../Constant";
import { validatePercentageRange } from "../../../CreateSku/helper";
import {
  ltrimString,
  isStringHavingSpecialChars,
} from "../../../../utils/Validator";
import ApproveSkuModal from "../../../../components/ApproveSkuModal";
import RejectSkuModal from "../../../../components/RejectSkuModal/RejectSkuModal";
import { AppDispatch } from "../../../../store";
import {
  approveNewCost,
  rejectNewCost,
  resetCostUpdateState,
} from "../../../../store/slices/CTDBBMOCostUpdateSlice";

interface CostUpdateRequestProps {
  formFields: any;
  skuResponse: any;
  UpdateCostId: string | null;
  disableCostUpdate: boolean;
  hideCostUpdate: boolean;
}

const CostUpdateRequest: React.FC<CostUpdateRequestProps> = ({
  formFields,
  skuResponse,
  UpdateCostId,
  disableCostUpdate,
  hideCostUpdate = false,
}) => {
  const { cost, CostMetricNSR, Indexation, skuNumber, skuShortDescription } =
    formFields;
  const navigate = useNavigate();

  const [newCost, setNewCost] = useState("");
  const [newIndexation, setNewIndexation] = useState("");
  const [newNSR, setNewNSR] = useState("");
  const [publishDate, setPublishDate] = useState<Date | null>(null);
  const [oldPublishDate, setOldPublishDate] = useState<Date | null>(null);
  const [showSkuApprovalModal, setShowSkuApprovalModal] = useState(false);
  const [showSkuRejectModal, setShowSkuRejectModal] = useState(false);
  const [costUpdateRequestError, setCostUpdateRequestError] = useState("");
  const [rejectReasonApiError, setRejectReasonApiError] = useState("");
  const [approveNewCostLoading, setIsApproveNewCostLoading] = useState(false);
  const [rejectNewCostLoading, setIsRejectNewCostLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [fileBackendError, setFileBackendError] = useState(false);
  useEffect(() => {
    const newCostVal = validateDecimalValue(skuResponse?.UpdateCost ?? "");
    const oldPublishDateVal = skuResponse?.oldPublishDate ?? "";
    const newIndexationVal = validateDecimalValue(
      skuResponse?.UpdateIndexation ?? "",
    );
    const newNSRVal = validateDecimalValue(skuResponse?.UpdateNSR ?? "");
    const publishDateVal = skuResponse?.UpdatePublishDtm ?? "";
    setNewCost(newCostVal);
    setNewIndexation(newIndexationVal || Indexation);
    setNewNSR(newNSRVal || CostMetricNSR);
    const publishDateOrg = convertDateStringtoDate(publishDateVal);
    const oldPublishDateOrg = convertDateStringtoDate(oldPublishDateVal);
    setPublishDate(publishDateOrg);
    setOldPublishDate(oldPublishDateOrg);
  }, [skuResponse, Indexation, CostMetricNSR]);

  const [newCostError, setNewCostError] = useState({
    isError: false,
    message: "",
    validation: ["required"],
    label: "New Cost Basis",
  });
  const [newIndexationError, setNewIndexationError] = useState({
    isError: false,
    message: "",
    validation: ["validatePercentageRange"],
    label: "New Indexation",
  });
  const [newNSRError, setNewNSRError] = useState({
    isError: false,
    message: "",
    validation: [""],
    label: "New NSR",
  });
  const [publishDateError, setPublishDateError] = useState({
    isError: false,
    message: "",
    validation: ["required"],
    label: "Publish Date",
  });

  const getMinimumPublishDate = (publishDate: Date | null) => {
    if (publishDate && !isNaN(publishDate.getTime())) {
      const minDateConverted = isDatePassed(publishDate)
        ? convertDateStringtoDate(getLocalDateStringYYYYFormat(publishDate))
        : skuResponse !== null
          ? convertDateStringtoDate(skuResponse?.UpdatePublishDtm)
          : new Date();
      return minDateConverted === null ? new Date(NaN) : minDateConverted;
    }
    return new Date(NaN);
  };
  const validateDecimalValue = (amt: any) => {
    if (amt === null || amt === undefined) {
      return "";
    }
    amt = String(amt);
    if (
      formatUSDWithCommas(amt).length > 0 &&
      formatUSDWithCommas(amt).length < 9
    ) {
      if (!amt.includes(".")) {
        amt = amt + ".00";
      } else {
        const [intPart, decPart] = amt.split(".");
        const newInt = intPart || "0";
        const newDec = decPart.padEnd(2, "0").slice(0, 2);
        amt = newInt + "." + newDec;
      }
    }
    return amt;
  };

  const handleCostChangeBlur = (event: {
    target: { name: string; value: string };
  }) => {
    const { name, value } = event.target;
    let val = value.replace(/[^0-9.]/g, "");
    val = val.replace(/(\..*)\./g, "$1");
    val = validateDecimalValue(val);
    if (name === "newCost") {
      setNewCost(val);
    } else if (name === "newIndexation") {
      setNewIndexation(val);
    } else if (name === "newNSR") {
      setNewNSR(val);
    }
  };

  const handleNewCostChange = (event: {
    target: { name: string; value: string };
  }) => {
    const { name, value } = event.target;
    let val = ltrimString(value);

    // Prevent special characters except for numbers and decimal point
    if (isStringHavingSpecialChars(val)) {
      return;
    }
    if (
      (name === "newCost" || name === "newNSR" || name === "newIndexation") &&
      val != null &&
      val != ""
    ) {
      val = val.replace(/[^0-9.]/g, "");
      val = val.replace(/(\..*)\./g, "$1");

      if (formatUSDWithCommas(val).length > 9) {
        if (val.endsWith(".")) {
          val = val.slice(0, -1);
        } else if (val.includes(".")) {
          const [intPart, decPart] = val.split(".");
          val = intPart.slice(0, 6) + "." + decPart.slice(0, 2);
        } else {
          val = val.slice(0, 8);
        }
      }

      if (val && val.includes(".")) {
        const [intPart, decPart] = val.split(".");
        val = intPart.slice(0, 6) + "." + decPart.slice(0, 2);
      }
    }
    if (name === "newCost") {
      setNewCost(val);
      validateNewCostField("newCost", val, newCostError, setNewCostError);
    } else if (name === "newIndexation") {
      setNewIndexation(val);
      validateNewCostField(
        "newIndexation",
        val,
        newIndexationError,
        setNewIndexationError,
      );
    } else if (name === "newNSR") {
      setNewNSR(val);
      validateNewCostField("newNSR", val, newNSRError, setNewNSRError);
    }
  };

  const handlePublishDateChange = (date: any) => {
    setPublishDate(date);
    if (date) {
      setPublishDateError({
        ...publishDateError,
        isError: false,
        message: "",
      });
    } else {
      setPublishDateError({
        ...publishDateError,
        isError: true,
        message: VALIDATION_ERROR_MESSAGES.REQUIRED,
      });
    }
  };

  const validateNewCostField = (
    fieldName: "newCost" | "newIndexation" | "newNSR" | "publishDate",
    value: string | Date | null,
    errorState: typeof newCostError,
    setErrorState: React.Dispatch<React.SetStateAction<typeof newCostError>>,
  ): boolean => {
    const validatorArray = errorState.validation;

    if (validatorArray.includes("required")) {
      if (value === "" || value === undefined || value === null) {
        setErrorState({
          ...errorState,
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.REQUIRED,
        });
        return false;
      }
    }

    if (
      fieldName === "newCost" &&
      (value === "" || value === undefined || value === null)
    ) {
      setErrorState({
        ...errorState,
        isError: false,
        message: VALIDATION_ERROR_MESSAGES.REQUIRED,
      });
    }
    if (
      fieldName === "newIndexation" &&
      validatorArray.includes("validatePercentageRange") &&
      value &&
      value !== "%"
    ) {
      if (value === null || value === "" || validatePercentageRange(value)) {
        setErrorState({
          ...errorState,
          isError: false,
          message: "",
        });
      } else {
        setErrorState({
          ...errorState,
          isError: true,
          message: VALIDATION_ERROR_MESSAGES.PERCENTAGE_RANGE_ERROR,
        });
        return false;
      }
    }

    setErrorState({
      ...errorState,
      isError: false,
      message: "",
    });
    return true;
  };

  const handleSKURejection = async (
    action?: string,
    msg?: string,
    skuId?: string,
  ) => {
    try {
      setRejectReasonApiError("");
      setIsRejectNewCostLoading(true);
      action = "skurejectcost";
      const rejectRequest = {
        Action: action,
        SkuId: skuId || skuNumber,
        Message: msg,
      };
      const response = await dispatch(rejectNewCost(rejectRequest));
      if (response.payload.Status === "success") {
        setIsRejectNewCostLoading(false);
        navigate("/ct/bmo");
      } else {
        setIsRejectNewCostLoading(false);
        setRejectReasonApiError(
          response?.payload?.message ||
            "An error occurred while rejecting cost update request. Please try again.",
        );
      }
    } catch {
      setIsRejectNewCostLoading(false);
      setRejectReasonApiError(
        "An error occurred while rejecting cost update request. Please try again.",
      );
    }
  };

  const validateFinalData = async (actionName: string) => {
    setCostUpdateRequestError("");
    let isValid = true;
    if (actionName === "approve") {
      if (
        !validateNewCostField("newCost", newCost, newCostError, setNewCostError)
      ) {
        isValid = false;
      }
      if (
        newIndexation &&
        !validateNewCostField(
          "newIndexation",
          newIndexation,
          newIndexationError,
          setNewIndexationError,
        )
      ) {
        isValid = false;
      }
      if (
        newNSR &&
        !validateNewCostField("newNSR", newNSR, newNSRError, setNewNSRError)
      ) {
        isValid = false;
      }
      if (
        !validateNewCostField(
          "publishDate",
          publishDate,
          publishDateError,
          setPublishDateError,
        )
      ) {
        isValid = false;
      }
    }
    if (!isValid) {
      setShowSkuRejectModal(false);
      setShowSkuApprovalModal(false);
      return;
    } else {
      if (actionName === "approve") {
        setShowSkuApprovalModal(true);
        setShowSkuRejectModal(false);
      } else if (actionName === "reject") {
        setRejectReasonApiError("");
        if (isDatePassed(publishDate)) {
          handleSKURejection(
            "skurejectcost",
            "The SKU could not be approved because the cost published date has passed.",
          );
        } else {
          setShowSkuApprovalModal(false);
          setShowSkuRejectModal(true);
        }
      }
    }
    return isValid;
  };

  const handleSKUApproval = async (action: string, file: File) => {
    try {
      setIsApproveNewCostLoading(true);
      const approvalRequest = {
        Action: action,
        SkuId: skuNumber,
        UpdateCost: Number(newCost),
        UpdateIndexation:
          newIndexation && newIndexation != null && newIndexation != ""
            ? parseFloat(newIndexation)
            : skuResponse?.Indexation != null && skuResponse?.Indexation !== ""
              ? parseFloat(String(skuResponse?.Indexation))
              : null,
        UpdateNSR:
          newNSR && newNSR != null && newNSR != ""
            ? Number(newNSR)
            : skuResponse?.CostMetricNSR != null &&
                skuResponse?.CostMetricNSR !== ""
              ? Number(skuResponse?.CostMetricNSR)
              : null,
        UpdatePublishdtm: formatToLocalYYYYMMDD(publishDate) || "",
        file: file,
        UpdateCostId: UpdateCostId,
      };
      const response = await dispatch(approveNewCost(approvalRequest));
      if (response.payload.Status === "success") {
        setIsApproveNewCostLoading(false);
        navigate("/ct/bmo");
      } else if (response?.payload?.message === "Invalid file") {
        setIsApproveNewCostLoading(false);
        dispatch(resetCostUpdateState());
        setFileBackendError(true);
      } else {
        setIsApproveNewCostLoading(false);
        setCostUpdateRequestError(
          response?.payload?.status === 400 && response?.payload?.message
            ? response.payload.message
            : "An error occurred while approving cost update request. Please try again. ",
        );
      }
    } catch {
      setIsApproveNewCostLoading(false);
      setCostUpdateRequestError(
        "An error occurred while approving cost update request. Please try again. ",
      );
    }
  };
  const getCostUpdateMessage = (status: string | null) => {
    if (status === "skuupdate_rejected") {
      return (
        <span>
          This cost update request for{" "}
          <b>
            Cost Basis ({formatValueInMoneyFormat(skuResponse?.UpdateCost)}
            ), Publishing Date (
            {new Date(skuResponse?.UpdatePublishDtm).toLocaleDateString(
              "en-GB",
              {
                timeZone: "UTC",
                day: "2-digit",
                month: "long",
                year: "numeric",
              },
            )}
            )
          </b>{" "}
          has been rejected because <b>"{skuResponse?.RejectReason}"</b>
        </span>
      );
    } else if (status === "skuupdate_pending") {
      return (
        <span>This cost update request has been approved by BMO Office.</span>
      );
    } else {
      return (
        <span>
          Please review the cost update request
          <span className="review-date">
            {""} before {""}
            {new Date(skuResponse?.UpdatePublishDtm).toLocaleDateString(
              "en-GB",
              {
                timeZone: "UTC",
                day: "2-digit",
                month: "long",
                year: "numeric",
              },
            )}{" "}
          </span>
          to avoid delays in publishing the updates.
        </span>
      );
    }
  };

  const getheaderMessage = () => {
    return isDatePassed(publishDate) &&
      skuResponse?.UpdateStatusCd !== "skuupdate_rejected" ? (
      <div className="motif-row review-text">
        <span>
          The cost update requested for publishing on
          <span className="review-date">
            {" "}
            {new Date(skuResponse?.UpdatePublishDtm).toLocaleDateString(
              "en-GB",
              {
                timeZone: "UTC",
                day: "2-digit",
                month: "long",
                year: "numeric",
              },
            )}{" "}
          </span>
          has expired. Please "Reject" this request so the user can submit a new
          one. (Automatic cancellation of expired requests is planned for a
          future release.)
        </span>
      </div>
    ) : (
      <div className="motif-row review-text">
        {getCostUpdateMessage(skuResponse?.UpdateStatusCd)}
      </div>
    );
  };
  const getNewCostStatus = (status: string | null) => {
    switch (status) {
      case "skuupdate_submitted":
        return (
          <MotifChip
            className="pending-approval"
            title="Pending Approval"
            variant="warning"
            dotSide="left"
          >
            Pending Approval
          </MotifChip>
        );
      case "skuupdate_pending":
        return (
          <MotifChip
            title="Approved - Scheduled for Publish"
            variant="success"
            dotSide="left"
            className="pending-approval"
          >
            Approved - Scheduled for Publish
          </MotifChip>
        );
      case "skuupdate_rejected":
        return (
          <MotifChip
            title="Rejected"
            variant="error"
            dotSide="left"
            className="pending-approval"
          >
            Rejected
          </MotifChip>
        );
      default:
        return (
          <MotifChip
            title="Pending Approval"
            variant="warning"
            dotSide="left"
            className="pending-approval"
          >
            Pending Approval
          </MotifChip>
        );
    }
  };
  return (
    <>
      <div className={`cost-update-section ${hideCostUpdate ? "hide" : ""}`}>
        <div className="motif-row headingCostUpdate">
          <div className="title">
            Cost Update Request
            {getNewCostStatus(skuResponse?.UpdateStatusCd)}
          </div>
        </div>
        {getheaderMessage()}
        <div className="motif-row form-item-row first-row">
          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="cost-update-label">{OLD_COST_BASIS}</div>
                <div className="inputField">
                  <InputBox
                    name={"oldCost"}
                    ariaLabel=""
                    onChange={() => {}}
                    value={formatUSDWithCommas(cost)}
                    prefix="$USD"
                    hideClearButton={true}
                    placeholder=""
                    readonly={true}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="cost-update-label">{OLD_INDEXATION}</div>
                <div className="inputField indexation-input-field">
                  <InputBox
                    name={"oldIndexation"}
                    ariaLabel=""
                    onChange={() => {}}
                    value={Indexation}
                    suffix={Indexation ? "%" : ""}
                    hideClearButton={true}
                    placeholder={INDEXATION_PLACEHOLDER}
                    readonly={true}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="cost-update-label">{OLD_NSR}</div>
                <div className="inputField">
                  <InputBox
                    name={"oldNSR"}
                    ariaLabel=""
                    onChange={() => {}}
                    value={formatUSDWithCommas(CostMetricNSR)}
                    hideClearButton={true}
                    placeholder={""}
                    readonly={true}
                    prefix="$USD"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item date-field">
                <div className="cost-update-label">{OLD_COST_PUBLISH_DATE}</div>
                <MotifDatePicker
                  clearIcon={null}
                  dayPlaceholder="DD"
                  format="dd/MM/yyyy"
                  maxDate={new Date(NaN)}
                  minDate={new Date(NaN)}
                  monthPlaceholder="MM"
                  disabled={true}
                  onCalendarClose={() => {}}
                  onCalendarOpen={() => {}}
                  onChange={() => {}}
                  value={oldPublishDate}
                  yearPlaceholder="YYYY"
                />
              </div>
            </div>
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item publish-date-text">
                Last cost update or SKU publish date
              </div>
            </div>
          </div>
        </div>
        <div className="motif-row form-item-row first-row">
          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="inputField">
                  <InputBox
                    name={"newCost"}
                    ariaLabel={NEW_COST_BASIS}
                    onBlur={handleCostChangeBlur}
                    onChange={handleNewCostChange}
                    value={formatUSDWithCommas(newCost || "")}
                    prefix="$USD"
                    hideClearButton={true}
                    placeholder=""
                    required={true}
                    readonly={disableCostUpdate}
                  />
                  {newCostError ? (
                    <MotifErrorMessage
                      aria-live="assertive"
                      role="alert"
                      id="aria-error-primaryList"
                    >
                      {newCostError.message}
                    </MotifErrorMessage>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="cost-update-label">{NEW_INDEXATION}</div>
                <div className="inputField indexation-input-field">
                  <InputBox
                    name={"newIndexation"}
                    ariaLabel={""}
                    onChange={handleNewCostChange}
                    value={newIndexation}
                    suffix={newIndexation ? "%" : ""}
                    hideClearButton={true}
                    placeholder={INDEXATION_PLACEHOLDER}
                    onBlur={handleCostChangeBlur}
                    readonly={disableCostUpdate}
                  />
                  {newIndexationError ? (
                    <MotifErrorMessage
                      aria-live="assertive"
                      role="alert"
                      id="aria-error-primaryList"
                    >
                      {newIndexationError.message}
                    </MotifErrorMessage>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item">
                <div className="cost-update-label">{NEW_NSR}</div>
                <div className="inputField">
                  <InputBox
                    ariaLabel={""}
                    name={"newNSR"}
                    onChange={handleNewCostChange}
                    onBlur={handleCostChangeBlur}
                    value={formatUSDWithCommas(newNSR)}
                    hideClearButton={true}
                    prefix="$USD"
                    placeholder={""}
                    readonly={disableCostUpdate}
                  />
                  {newNSRError ? (
                    <MotifErrorMessage
                      aria-live="assertive"
                      role="alert"
                      id="aria-error-primaryList"
                    >
                      {newNSRError.message}
                    </MotifErrorMessage>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="motif-col-lg">
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item date-field">
                <MotifFormField aria-label="Form Field Date Picker">
                  <MotifLabel aria-label="Date">
                    {NEW_COST_PUBLISH_DATE}
                    <span className="required">*</span>
                  </MotifLabel>

                  <MotifDatePicker
                    clearIcon={null}
                    dayPlaceholder="DD"
                    format="dd/MM/yyyy"
                    maxDate={new Date(NaN)}
                    minDate={
                      publishDate
                        ? getMinimumPublishDate(publishDate)
                        : new Date(NaN)
                    }
                    monthPlaceholder="MM"
                    disabled={isDatePassed(publishDate) || disableCostUpdate}
                    onChange={handlePublishDateChange}
                    value={publishDate}
                    yearPlaceholder="YYYY"
                  />
                </MotifFormField>
              </div>
            </div>
            <div className="motif-row">
              <div className="motif-col-lg-12 form-item publish-date-text">
                Publishing occurs at 00:00 hrs UTC
              </div>
            </div>
          </div>
        </div>
        <div className="motif-row form-item-row">
          <div className="motif-col-lg-12">
            <div className="motif-row">
              <div className="motif-col-lg-9"></div>
              <div
                className={`motif-col-lg-3 btn-container-box ${(disableCostUpdate && !isDatePassed(publishDate)) || skuResponse?.UpdateStatusCd === "skuupdate_rejected" ? "hide" : ""}`}
              >
                <MotifButton
                  size="small"
                  type="submit"
                  variant="primary"
                  className={`approve-button ${isDatePassed(publishDate) ? "hide" : ""}`}
                  onClick={() => {
                    validateFinalData("approve");
                  }}
                  disabled={approveNewCostLoading}
                >
                  {
                    <MotifIcon
                      fill="#fff"
                      stroke="#fff"
                      iconFunction={IconoirCheck}
                      title="Computer"
                      size="24"
                    />
                  }{" "}
                  APPROVE
                </MotifButton>
                <MotifButton
                  size="small"
                  type="button"
                  variant="primary"
                  className="reject-button"
                  onClick={() => {
                    validateFinalData("reject");
                  }}
                  disabled={rejectNewCostLoading}
                  loading={rejectNewCostLoading && isDatePassed(publishDate)}
                >
                  {
                    <MotifIcon
                      iconFunction={MotifNavigationIcClose24px}
                      title="Reject"
                      size="24"
                      fill="white"
                    />
                  }{" "}
                  REJECT
                </MotifButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ApproveSkuModal
        show={showSkuApprovalModal}
        setShow={setShowSkuApprovalModal}
        btnLabel="Approve SKU"
        approveHandler={handleSKUApproval}
        selectedSkuName={skuShortDescription}
        selectedSku={skuNumber}
        requestType="Cost Update"
        isLoading={approveNewCostLoading}
        apiErrorMessage={costUpdateRequestError}
        onClearApiError={() => setCostUpdateRequestError("")}
        fileBackendError={fileBackendError}
        setFileBackendError={setFileBackendError}
      />
      <RejectSkuModal
        show={showSkuRejectModal}
        setShow={setShowSkuRejectModal}
        rejectHandler={handleSKURejection}
        selectedSkuName={skuShortDescription}
        selectedSku={skuNumber}
        requestType="Cost Update"
        isLoading={rejectNewCostLoading}
        apiErrorMessage={rejectReasonApiError}
        onClearApiError={() => setRejectReasonApiError("")}
      />
    </>
  );
};

export default CostUpdateRequest;
